import { notifyUpdateAvailable, setServiceWorkerActive, setUpdateApplier } from './updateSignal'

// service worker registration. imported only from main.tsx — it pulls in
// `virtual:pwa-register`, which exists solely inside a vite build, so any module that
// imports this one becomes unloadable under vitest.
//
// a waiting worker is a strictly better redeploy signal than polling /version.json: it
// only appears once the new build is fully downloaded, so accepting the update prompt
// activates a complete build rather than racing the network. the poller remains the
// fallback wherever no worker registers.

// how often an open tab re-checks for a newer worker. matches the interval the version
// poller used, so a long-lived tab still learns about a deploy within about a minute.
export const SW_UPDATE_CHECK_INTERVAL_MS = 60_000

export async function registerPwa(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    setServiceWorkerActive(false)
    return
  }

  try {
    const { registerSW } = await import('virtual:pwa-register')
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh: notifyUpdateAvailable,
      // `registerSW` resolves before the browser has accepted the worker and never throws
      // on a failed registration — it routes those here. taking success for granted would
      // leave the fallback version poller switched off with no worker to replace it, so
      // deploy detection would be silently dead.
      onRegisterError() {
        setServiceWorkerActive(false)
      },
      onRegisteredSW(_swUrl, registration) {
        if (!registration) {
          setServiceWorkerActive(false)
          return
        }
        setServiceWorkerActive(true)
        // registration alone only checks for a new worker on load; an open tab has to keep
        // asking, and should ask straight away when the user returns to it
        const check = () => void registration.update()
        window.setInterval(check, SW_UPDATE_CHECK_INTERVAL_MS)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check()
        })
      },
    })
    setUpdateApplier(() => void updateSW(true))
  } catch {
    // registration fails on an insecure origin or with workers disabled; the version
    // poller then covers deploy detection
    setServiceWorkerActive(false)
  }
}
